/**
 * useLegislatorFiltering Hook
 * 
 * Shared hook for filter state management, sorting, and filter options fetching.
 * Used by LegislatorsPage, LegislatorsTab, and LegislatorsReport.
 */

import { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { matchesPartyFilter } from '../../utils/partyUtils';
import { CURRENT_SESSION, useSessions } from '../../hooks/useSessions';
import {
  BaseLegislator,
  LegislatorFilterState,
  LegislatorFilterOptions,
  SortField,
  SortState,
  DEFAULT_FILTER_STATE,
  DEFAULT_SORT_STATE,
} from './types';

interface UseLegislatorFilteringOptions {
  /** Initial filter state (override defaults) */
  initialFilters?: Partial<LegislatorFilterState>;
  /** Initial sort state */
  initialSort?: Partial<SortState>;
  /** Debounce delay for search (ms) */
  debounceDelay?: number;
  /** Callback when filters change (for server-side filtering) */
  onFiltersChange?: (filters: LegislatorFilterState) => void;
}

interface UseLegislatorFilteringReturn {
  // Filter state
  filters: LegislatorFilterState;
  setFilters: React.Dispatch<React.SetStateAction<LegislatorFilterState>>;
  updateFilter: (key: keyof LegislatorFilterState, value: string) => void;
  clearFilters: () => void;
  
  // Debounced search
  debouncedSearchQuery: string;
  
  // Sort state
  sort: SortState;
  setSort: React.Dispatch<React.SetStateAction<SortState>>;
  handleSort: (field: string) => void;
  
  // Filter options from API
  filterOptions: LegislatorFilterOptions;
  isLoadingOptions: boolean;
  refetchFilterOptions: (chamberFilter?: string) => Promise<void>;
  
  // Utility functions
  applyFiltersToData: <T extends BaseLegislator>(data: T[]) => T[];
  applySortToData: <T extends BaseLegislator>(data: T[]) => T[];
}

/**
 * Hook for managing legislator filtering, sorting, and filter options
 */
export function useLegislatorFiltering(
  options: UseLegislatorFilteringOptions = {}
): UseLegislatorFilteringReturn {
  const {
    initialFilters = {},
    initialSort = {},
    debounceDelay = 300,
    onFiltersChange,
  } = options;

  // Resolves the 'current' sentinel for the client-side session filter below.
  const { currentSessionKey } = useSessions();

  // ==========================================================================
  // Filter State
  // ==========================================================================
  
  const [filters, setFilters] = useState<LegislatorFilterState>({
    ...DEFAULT_FILTER_STATE,
    ...initialFilters,
  });
  
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(filters.searchQuery);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(filters.searchQuery);
    }, debounceDelay);

    return () => clearTimeout(timer);
  }, [filters.searchQuery, debounceDelay]);

  // Notify parent when filters change
  useEffect(() => {
    onFiltersChange?.(filters);
  }, [filters, onFiltersChange]);

  const updateFilter = useCallback((key: keyof LegislatorFilterState, value: string) => {
    setFilters(prev => {
      const newFilters = { ...prev, [key]: value };

      // If chamber changes, reset committee and district filters
      if (key === 'chamber') {
        newFilters.committee = 'All committees';
        newFilters.district = 'All districts';
      }

      return newFilters;
    });
  }, []);

  const clearFilters = useCallback(() => {
    // Reset to defaults - use DEFAULT_FILTER_STATE which has CURRENT_SESSION as sessionYear
    setFilters(DEFAULT_FILTER_STATE);
  }, []);

  // ==========================================================================
  // Sort State
  // ==========================================================================
  
  const [sort, setSort] = useState<SortState>({
    ...DEFAULT_SORT_STATE,
    ...initialSort,
  });

  const handleSort = useCallback((field: string) => {
    const sortField = field as SortField;
    setSort(prev => ({
      field: sortField,
      direction: prev.field === sortField && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  }, []);

  // ==========================================================================
  // Filter Options
  // ==========================================================================
  
  const [filterOptions, setFilterOptions] = useState<LegislatorFilterOptions>({
    chambers: [],
    parties: [],
    districts: [],
    committees: [],
    sessionYears: [],
    currentSessionYear: null,
  });
  
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);

  const refetchFilterOptions = useCallback(async (chamberFilter?: string) => {
    try {
      setIsLoadingOptions(true);
      const params = new URLSearchParams();
      if (chamberFilter && chamberFilter !== 'All') {
        params.append('chamber', chamberFilter);
      }
      
      const url = `/legislators/filter-options${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await api.get(url);
      
      if (response.data.success) {
        setFilterOptions(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching filter options:', error);
    } finally {
      setIsLoadingOptions(false);
    }
  }, []);

  // Fetch filter options on mount and when chamber changes
  // When chamber is 'All', fetch all committees; otherwise filter by selected chamber
  useEffect(() => {
    const chamberFilter = filters.chamber === 'All' ? undefined : filters.chamber;
    refetchFilterOptions(chamberFilter);
  }, [filters.chamber, refetchFilterOptions]);

  // ==========================================================================
  // Data Transformation Functions
  // ==========================================================================

  /**
   * Apply client-side filtering to legislator data
   * Note: Data is normalized to camelCase by useLegislatorData hook
   */
  const applyFiltersToData = useCallback(<T extends BaseLegislator>(data: T[]): T[] => {
    return data.filter(legislator => {
      // Type assertion for normalized camelCase properties
      const leg = legislator as T & {
        fullName?: string;
        firstName?: string;
        lastName?: string;
        partyAffiliation?: string;
        sessionYear?: string | number;
        sessionKey?: string;
      };

      // Search filter
      if (debouncedSearchQuery) {
        const query = debouncedSearchQuery.toLowerCase();
        const searchableText = [
          leg.fullName,
          leg.firstName,
          leg.lastName,
          leg.chamber,
          leg.district,
          leg.partyAffiliation,
          leg.committees,
        ].filter(Boolean).join(' ').toLowerCase();
        
        if (!searchableText.includes(query)) {
          return false;
        }
      }

      /*
        Session filter (skip for "All").

        'current' is the sentinel the server resolves against the registry, so it
        is resolved here too rather than compared literally — a literal compare
        against a row's '2026' matches nothing and empties the list. Until the
        registry has loaded, 'current' filters nothing: showing every row briefly
        is recoverable, showing none reads as "the data is gone".
      */
      const selectedSession =
        filters.sessionYear === CURRENT_SESSION ? currentSessionKey : filters.sessionYear;
      if (selectedSession && selectedSession !== '' && selectedSession !== 'All') {
        const legislatorSession = String(leg.sessionKey || leg.sessionYear || '');
        if (legislatorSession !== selectedSession) {
          return false;
        }
      }

      // Chamber filter
      if (filters.chamber !== 'All' && leg.chamber !== filters.chamber) {
        return false;
      }

      // Committee filter
      if (filters.committee !== 'All committees') {
        const committees = leg.committees?.toLowerCase() || '';
        if (!committees.includes(filters.committee.toLowerCase())) {
          return false;
        }
      }

      // Party filter
      if (filters.party !== 'All parties' && !matchesPartyFilter(leg.partyAffiliation, filters.party)) {
        return false;
      }

      // District filter
      if (filters.district !== 'All districts' && leg.district !== filters.district) {
        return false;
      }

      return true;
    });
  }, [debouncedSearchQuery, filters, currentSessionKey]);

  /**
   * Apply client-side sorting to legislator data
   * Note: Data is normalized to camelCase by useLegislatorData hook
   */
  const applySortToData = useCallback(<T extends BaseLegislator>(data: T[]): T[] => {
    return [...data].sort((a, b) => {
      // Type assertion for normalized camelCase properties
      const legA = a as T & {
        lastName?: string;
        firstName?: string;
        partyAffiliation?: string;
        sponsoredBillsCount?: number;
      };
      const legB = b as T & {
        lastName?: string;
        firstName?: string;
        partyAffiliation?: string;
        sponsoredBillsCount?: number;
      };

      let comparison = 0;

      switch (sort.field) {
        case 'name':
          // Sort by last name, then first name
          comparison = (legA.lastName || '').localeCompare(legB.lastName || '');
          if (comparison === 0) {
            comparison = (legA.firstName || '').localeCompare(legB.firstName || '');
          }
          break;
          
        case 'district':
          comparison = parseInt(legA.district || '0') - parseInt(legB.district || '0');
          break;
          
        case 'party':
          comparison = (legA.partyAffiliation || '').localeCompare(legB.partyAffiliation || '');
          break;
          
        case 'chamber':
          comparison = (legA.chamber || '').localeCompare(legB.chamber || '');
          break;
          
        case 'sponsored_bills_count': {
          const aCount = legA.sponsoredBillsCount || 0;
          const bCount = legB.sponsoredBillsCount || 0;
          comparison = aCount - bCount;
          break;
        }
          
        default:
          comparison = 0;
      }

      return sort.direction === 'asc' ? comparison : -comparison;
    });
  }, [sort]);

  // ==========================================================================
  // Return
  // ==========================================================================

  return {
    // Filter state
    filters,
    setFilters,
    updateFilter,
    clearFilters,
    
    // Debounced search
    debouncedSearchQuery,
    
    // Sort state
    sort,
    setSort,
    handleSort,
    
    // Filter options
    filterOptions,
    isLoadingOptions,
    refetchFilterOptions,
    
    // Utility functions
    applyFiltersToData,
    applySortToData,
  };
}

export default useLegislatorFiltering;
